// frontend/src/services/api.js
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1/';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers['Authorization'] = `Token ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --- Authentication Services ---
export const loginUser = async (credentials) => {
  try {
    const loginResponse = await apiClient.post('auth/login/', credentials);
    const token = loginResponse.data.key;
    if (token) {
      localStorage.setItem('authToken', token);
      const userDetailsResponse = await apiClient.get('auth/user/');
      return { token: token, user: userDetailsResponse.data };
    } else {
      throw new Error('Login successful but no token key was received.');
    }
  } catch (error) {
    console.error("API: Login error:", error.response?.data || error.message);
    throw error.response?.data || new Error('Login failed. Please check your credentials.');
  }
};

export const registerUser = async (userData) => {
  try {
    const response = await apiClient.post('auth/registration/', userData);
    return response.data;
  } catch (error) {
    console.error("API: Registration error:", error.response?.data || error.message);
    throw error.response?.data || new Error('Registration failed. Please try again.');
  }
};

export const logoutUser = async () => {
  try {
    await apiClient.post('auth/logout/');
  } catch (error) {
    console.error("API: Logout API call failed:", error.response?.data || error.message);
  } finally {
    localStorage.removeItem('authToken');
  }
};

export const getUserDetails = async () => {
  try {
    const response = await apiClient.get('auth/user/');
    // Ensure backend returns subscribed_source_ids: e.g., { ..., subscribed_source_ids: [1, 2, 3] }
    return response.data;
  } catch (error) {
    console.error("API: Error fetching user details:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch user details.');
  }
};

export const updateUserDetails = async (userData) => {
  try {
    // userData should contain fields like { first_name: "...", last_name: "..." }
    const response = await apiClient.put('auth/user/', userData); // or .patch
    return response.data;
  } catch (error) {
    console.error("API: Error updating user details:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to update user details.');
  }
};

export const changePassword = async (passwordData) => {
  try {
    // passwordData should be { new_password1: "...", new_password2: "...", old_password: "..." }
    const response = await apiClient.post('auth/password/change/', passwordData);
    return response.data;
  } catch (error) {
    console.error("API: Error changing password:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to change password.');
  }
};

// --- Source Services ---
export const getSources = async (page = 1, pageSize = 1000) => { // Increased default page size
  try {
    const response = await apiClient.get(`sources/?page=${page}&page_size=${pageSize}`);
    return response.data;
  } catch (error) {
    console.error("API: Error fetching sources:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch sources.');
  }
};

export const getAllSources = async () => {
  let allSources = [];
  let currentPage = 1;
  const pageSize = 1000; // Page size for individual requests while paginating
  let hasMorePages = true;

  try {
    while (hasMorePages) {
      // Directly use apiClient.get to control pagination and access the full response
      const response = await apiClient.get(`sources/?page=${currentPage}&page_size=${pageSize}`);
      const data = response.data; // Expects DRF paginated response: { count, next, previous, results }
      
      if (data.results && data.results.length > 0) {
        allSources = allSources.concat(data.results);
      }

      // Check if there's a 'next' URL to determine if there are more pages
      if (data.next) {
        currentPage++;
      } else {
        hasMorePages = false;
      }
    }
    return allSources; // Return the aggregated list of all source objects
  } catch (error) {
    console.error("API: Error fetching all sources:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch all sources.');
  }
};

export const addSource = async (sourceData) => {
  try {
    const response = await apiClient.post('sources/', sourceData);
    return response.data;
  } catch (error) {
    console.error("API: Error adding source:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to add source.');
  }
};

export const deleteSource = async (sourceId) => {
  try {
    const response = await apiClient.delete(`sources/${sourceId}/`);
    return response.data;
  } catch (error) {
    console.error("API: Error deleting source:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to delete source.');
  }
};

export const triggerSourceFetch = async (sourceId) => {
  try {
    const response = await apiClient.post(`sources/${sourceId}/fetch-now/`);
    return response.data;
  } catch (error) {
    console.error("API: Error triggering source fetch:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to trigger fetch for source.');
  }
};

export const subscribeToSource = async (sourceId) => {
  try {
    const response = await apiClient.post(`sources/${sourceId}/subscribe/`);
    return response.data;
  } catch (error) {
    console.error(`API: Error subscribing to source ${sourceId}:`, error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to subscribe to source.');
  }
};

export const unsubscribeFromSource = async (sourceId) => {
  try {
    const response = await apiClient.post(`sources/${sourceId}/unsubscribe/`);
    return response.data;
  } catch (error) {
    console.error(`API: Error unsubscribing from source ${sourceId}:`, error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to unsubscribe from source.');
  }
};

// --- Article Services ---
export const getArticles = async (params = {}) => {
  const {
    page = 1,
    page_size = 12, // Default page size to match usage in ResearchNewsFeedPage
    source_ids,
    topics,
    content_types,
    publication_date_after,
    publication_date_before,
    ids,
    subscribed_only, // New parameter for filtering by subscription
    ...otherFilters // Collect any other potential filters not explicitly destructured
  } = params;

  let queryParams = `?page=${page}&page_size=${page_size}`;

  if (source_ids) queryParams += `&source_ids=${encodeURIComponent(source_ids)}`;
  if (topics) queryParams += `&topics=${encodeURIComponent(topics)}`;
  if (content_types) queryParams += `&content_types=${encodeURIComponent(content_types)}`;
  if (publication_date_after) queryParams += `&publication_date_after=${encodeURIComponent(publication_date_after)}`;
  if (publication_date_before) queryParams += `&publication_date_before=${encodeURIComponent(publication_date_before)}`;
  if (ids) queryParams += `&ids=${encodeURIComponent(ids)}`;
  if (subscribed_only !== undefined) queryParams += `&subscribed_only=${subscribed_only}`; // Add the new parameter

  // Add any other filters that might have been passed and are not explicitly handled above
  for (const key in otherFilters) {
    if (otherFilters.hasOwnProperty(key) && otherFilters[key] !== undefined) {
      // Avoid re-adding page/page_size if they were somehow still in otherFilters
      if (key !== 'page' && key !== 'page_size') {
        queryParams += `&${encodeURIComponent(key)}=${encodeURIComponent(otherFilters[key])}`;
      }
    }
  }

  try {
    const response = await apiClient.get(`articles/${queryParams}`);
    return response.data;
  } catch (error) {
    console.error("API: Error fetching articles:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch articles.');
  }
};

export const triggerArticleClassification = async (articleId) => {
  try {
    const response = await apiClient.post(`articles/${articleId}/classify-now/`);
    return response.data;
  } catch (error) {
    console.error("API: Error triggering article classification:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to trigger classification for article.');
  }
};

// --- Topic Service ---
export const getUniqueTopics = async () => { // Fetches all unique topics
  try {
    const response = await apiClient.get('topics/'); 
    return response.data;
  } catch (error) {
    console.error("API: Error fetching unique topics:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch unique topics.');
  }
};

export const getCommonTopics = async (limit = 20) => { // New function for common topics
  try {
    const response = await apiClient.get(`common-topics/?limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error("API: Error fetching common topics:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch common topics.');
  }
};

export const getUniqueContentTypes = async () => { // New function for unique content types
  try {
    // Assuming you have an endpoint like /content-types/ in your backend
    // If not, you'll need to create it similar to UniqueTopicsView
    const response = await apiClient.get('content-types/'); 
    return response.data;
  } catch (error) {
    console.error("API: Error fetching unique content types:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch unique content types.');
  }
};

// --- Saved Article Services ---
export const getSavedArticles = async (page = 1, pageSize = 12) => {
  try {
    const response = await apiClient.get(`saved-articles/?page=${page}&page_size=${pageSize}`);
    // Return the full paginated response with results, count, next, previous
    return response.data;
  } catch (error) {
    console.error("API: Error fetching saved articles:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch saved articles.');
  }
};

export const saveArticle = async (articleId) => {
  try {
    const response = await apiClient.post('saved-articles/', { article_id: articleId });
    return response.data;
  } catch (error) {
    console.error("API: Error saving article:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to save article.');
  }
};

export const unsaveArticleByArticleId = async (articleId) => {
  try {
    const response = await apiClient.delete(`saved-articles/by-article/${articleId}/`);
    return response.data;
  } catch (error) {
    console.error("API: Error unsaving article by article ID:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to unsave article.');
  }
};

export const conductResearch = async (query) => {
  try {
    // Replace 'ai-research-analyst/' with your actual endpoint
    const response = await apiClient.post('ai-research-analyst/', { query });
    return response.data;
  } catch (error) {
    console.error("API: Error conducting research:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to conduct research.');
  }
};

export const getFeaturedArticle = async () => {
  try {
    const response = await apiClient.get('featured-articles/');
    return response.data // Expects a single article object or 404
  } catch (error) {
    if (error.response && error.response.status === 404) {
      console.log("API: No featured article found.");
      return null; // Explicitly return null if not found
    }
    console.error("API: Error fetching featured article:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch featured article.');
  }
};

// --- Daily Digest Service ---
export const getDailyDigest = async (date) => { // date in 'YYYY-MM-DD' format
  try {
    const params = date ? { date } : {};
    const response = await apiClient.get('daily-digest/', { params });
    return response.data; // Expected: { id, date, title, content, created_at }
  } catch (error) {
    console.error("API: Error fetching daily digest:", error.response?.data || error.message);
    if (error.response && error.response.status === 404) {
      return null; // Return null if digest for the date is not found
    }
    throw error.response?.data || new Error('Failed to fetch daily digest.');
  }
};

export const getTopicInsights = async (params = {}) => {
  try {
    // params could be { days: 7, top_n: 5 }
    const queryParams = new URLSearchParams(params).toString();
    const response = await apiClient.get(`topic-insights/?${queryParams}`);
    return response.data; // Expected: { topics_data: [{ topic_name, article_count_in_period, articles: [...] }] }
  } catch (error) {
    console.error("API: Error fetching topic insights:", error.response?.data || error.message);
    throw error.response?.data || new Error('Failed to fetch topic insights.');
  }
};
